/* eslint-disable no-param-reassign */
import {
  controlSectionsVulcan,
  monitorSectionsVulcan,
  protectSectionsVulcan,
  secureBusinessPlanVulcan,
  smartCameraSectionsVulcan,
  tsb15SectionPriceKeys,
  isTsbTakeoverEligibleOutlet
} from '../../../utils/constant';
import priceSelectionList from '../utils/getPriceSelectionList';
import getTotalsForSetOfTotals from '../utils/getTotalsForSetOfTotals';
import { pricingFields, setPricingFields } from './secureBusinessFieldNames';
import { getLocale } from '../../../misc';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import {
  getConsolidationSelectionTotals,
  getConsolidationTotals,
  getInstallationFeePaymentType
} from '../utils/utils';
import { getFieldValue } from '../../utils';

export const shouldMergeTsb15PriceKeys = (
  formFields,
  orderData,
  valueForCheck
) => {
  if (!orderData) return false;
  const locale = getLocale();
  const {
    vulcanWebform: { yes }
  } = locale === 'en-US' ? EN : FR;
  const outletId = orderData.salesRepOutlet;
  const valueOfYesForCheck = valueForCheck || yes;
  const existingSecurityYes =
    getFieldValue('existingSecurityEquipment', formFields) ===
    valueOfYesForCheck;
  return isTsbTakeoverEligibleOutlet(outletId) && existingSecurityYes;
};

/**
 * Returns the count of add-on equipment from tsb15SectionPriceKeys (takeover wired/wireless)
 * for Waived Installation Fee. Only counts when eligible for TSB15 (shouldMergeTsb15PriceKeys).
 * @param {Object} formFields - Form state
 * @param {Object} orderData - Order data (must include salesRepOutlet for eligibility check)
 * @returns {number}
 */
export const getTsb15AddOnEquipmentCount = (formFields, orderData) => {
  if (!orderData || !shouldMergeTsb15PriceKeys(formFields, orderData)) {
    return 0;
  }
  let count = 0;
  tsb15SectionPriceKeys.forEach(sectionKey => {
    const section = formFields[sectionKey];
    if (section && typeof section === 'object') {
      Object.keys(section).forEach(fieldKey => {
        if (
          fieldKey.indexOf('_dropdown') === -1 &&
          section[fieldKey] &&
          section[fieldKey].value
        ) {
          count += parseInt(section[fieldKey].value, 10) || 0;
        }
      });
    }
  });
  return count;
};

export const handleInstallationFeeDiscount = (
  fields,
  numberOfEquipments,
  isPercentageDiscount,
  installationFeePaymentOption = '',
  installationFeeAmount = 0
) => {
  const locale = getLocale();
  const {
    vulcanWebform: {
      vulWaivedInstallationFeeOptionTSB,
      vulDiscountForInstallationFeeOptionTSB
    }
  } = locale === 'en-US' ? EN : FR;
  const { options, value } = fields;

  const paymentType = getInstallationFeePaymentType(
    installationFeePaymentOption
  );
  const shouldShowPercentageDiscount =
    isPercentageDiscount && paymentType === 'oneTime';

  // Delete previous Installation Fee offers (fixed)
  const installationFeeOptionIndex = options.findIndex(
    option => option.fields.value === vulWaivedInstallationFeeOptionTSB
  );
  if (installationFeeOptionIndex > -1) {
    options.splice(installationFeeOptionIndex, 1);
    if (Array.isArray(value)) {
      const installationFeeIndex = value.findIndex(
        i => i === vulWaivedInstallationFeeOptionTSB
      );
      if (installationFeeIndex > -1) {
        value.splice(installationFeeIndex, 1);
      }
    }
  }

  // Remove the percentage-based discount option so it can be re-added with
  // a fresh price. Preserve the user's selection in `value` when the option
  // is still eligible to be shown; otherwise clear it.
  const discountOptionIndex = options.findIndex(
    option => option.fields.value === vulDiscountForInstallationFeeOptionTSB
  );
  if (discountOptionIndex > -1) {
    options.splice(discountOptionIndex, 1);
  }
  if (!shouldShowPercentageDiscount && Array.isArray(value)) {
    const discountIndex = value.findIndex(
      i => i === vulDiscountForInstallationFeeOptionTSB
    );
    if (discountIndex > -1) {
      value.splice(discountIndex, 1);
    }
  }

  if (shouldShowPercentageDiscount) {
    // Add percentage-based offer for Vulcan flow (20% EN / 20% FR)
    // Now the discount amounts are the same, but let's have this logic
    // in case we need to change it in the future.
    const discountRate = locale === 'en-US' ? 20 : 20;
    const discountLabel =
      locale === 'en-US'
        ? `${discountRate}% Off Installation Fee`
        : `${discountRate} % de réduction sur les frais d'installation`;
    const percentageDiscountAmount =
      Math.round(installationFeeAmount * discountRate) / 100;

    options.push({
      fields: {
        label: discountLabel,
        value: vulDiscountForInstallationFeeOptionTSB,
        oneTimePrice: -1 * percentageDiscountAmount // Negative = discount
      }
    });
  }
};

export const getSecureBusinessTotals = (
  formFields,
  priceSelections,
  orderData,
  equipmentBlobs
) => {
  const locale = getLocale();
  const {
    vulcanWebform: {
      vulMonitorTSB,
      vulControlTSB,
      vulProtectTSB,
      vulSmartCameraTSB
    }
  } = locale === 'en-US' ? EN : FR;

  // Equipment sections are `custom`/blob-shaped in the engine — never
  // tree-walked into fieldStates — so `formFields[section]` below is always
  // undefined when `formFields` is `engine.fieldStates`. `equipmentBlobs`
  // (each section's `engine.values[section]`, passed explicitly by the
  // caller) is what actually carries that data now.
  const sectionSource = equipmentBlobs || formFields;

  if (formFields[secureBusinessPlanVulcan]) {
    setPricingFields(formFields, false, equipmentBlobs);
  } else {
    setPricingFields(orderData, true);
  }

  if (shouldMergeTsb15PriceKeys(formFields, orderData)) {
    tsb15SectionPriceKeys.forEach(section => {
      if (sectionSource[section]) {
        formFields = { ...formFields, ...sectionSource[section] };
      }
    });
  }

  if (formFields[secureBusinessPlanVulcan]) {
    const planValue = formFields[secureBusinessPlanVulcan].value;

    // Map each plan type to its corresponding sections
    const planSectionsMap = {
      [vulMonitorTSB]: monitorSectionsVulcan,
      [vulProtectTSB]: protectSectionsVulcan,
      [vulControlTSB]: controlSectionsVulcan,
      [vulSmartCameraTSB]: smartCameraSectionsVulcan
    };

    const selectedSections = planSectionsMap[planValue];

    if (selectedSections && Array.isArray(selectedSections)) {
      selectedSections.forEach(section => {
        if (sectionSource[section]) {
          formFields = { ...formFields, ...sectionSource[section] };
        }
      });
    }
  }

  const secureBusinessTotals = getTotalsForSetOfTotals(
    pricingFields,
    formFields,
    priceSelections
  );

  return secureBusinessTotals;
};

export const getSecureBusinessPriceSelections = (formFields, orderData) => {
  const locale = getLocale();
  const {
    vulcanWebform: {
      vulMonitorTSB,
      vulControlTSB,
      vulProtectTSB,
      vulSmartCameraTSB
    }
  } = locale === 'en-US' ? EN : FR;

  if (shouldMergeTsb15PriceKeys(formFields, orderData)) {
    tsb15SectionPriceKeys.forEach(section => {
      if (formFields[section]) {
        formFields = { ...formFields, ...formFields[section] };
      }
    });
  }

  if (formFields[secureBusinessPlanVulcan]) {
    const planValue = formFields[secureBusinessPlanVulcan].value;

    // Map each plan type to its corresponding sections
    const planSectionsMap = {
      [vulMonitorTSB]: monitorSectionsVulcan,
      [vulProtectTSB]: protectSectionsVulcan,
      [vulControlTSB]: controlSectionsVulcan,
      [vulSmartCameraTSB]: smartCameraSectionsVulcan
    };

    const selectedSections = planSectionsMap[planValue];

    if (selectedSections && Array.isArray(selectedSections)) {
      selectedSections.forEach(section => {
        if (formFields[section]) {
          formFields = { ...formFields, ...formFields[section] };
        }
      });
    }
  }

  return priceSelectionList(pricingFields, formFields);
};

export const getTsbConsolidationTotals = tsbData => {
  return getConsolidationTotals(tsbData);
};

export const getTsbConsolidationPriceSelections = tsbData => {
  return getConsolidationSelectionTotals(tsbData, 'tsbData');
};
